import { useCallback, useEffect, useState } from 'react';
import { ApiError, type AgentRow, type Api, type Line, type LineInput, type TelephonyView, agentTitle } from '../api';
import type { T } from '../i18n';
import { DangerButton, Field } from '../ui';
import { errorText } from './AgentSettings';
import { SmsAccountCard } from './SmsAccount';
import { WhatsAppAccountCard } from './WhatsAppAccount';
import { ClosedDatesCard, NumberPlanCard, PlanCell } from './NumberPlan';

type Msg = { kind: 'ok' | 'error' | 'warn'; text: string };

/** Registration / server state → a pill kind and its label. */
const lineState = (l: Line, t: T): { kind: string; text: string } => {
  const r = l.status.registration;
  if (r === 'Registered') return { kind: 'published', text: t('lines.state.registered') };
  if (r === 'Rejected') return { kind: 'fail', text: t('lines.state.rejected') };
  if (r === 'unknown') return { kind: 'muted', text: t('lines.state.unknown') };
  if (l.status.server === 'offline') return { kind: 'fail', text: t('lines.state.unreachable') };
  return { kind: 'warn', text: t('lines.state.pending') };
};

/** A refused field names itself (which field, and what it may contain). */
const lineError = (t: T, e: unknown): string => {
  const field = e instanceof ApiError && e.code === 'SIP_FIELD_INVALID' ? String(e.detail.field ?? '') : '';
  return field && t(`lines.invalid.${field}`) !== `lines.invalid.${field}` ? t(`lines.invalid.${field}`) : errorText(t, e);
};

type TestState = { phase: 'idle' | 'testing' | 'ok' | 'rejected' | 'waiting' | 'unknown'; seconds?: number };
const TEST_TRIES = 12;
const TEST_EVERY_MS = 2500;

/** Polls a line's registration until the provider answers (or ~30 s pass). */
const runLineTest = async (api: Api, id: string, onTick: (s: TestState) => void): Promise<TestState> => {
  for (let i = 0; i < TEST_TRIES; i += 1) {
    onTick({ phase: 'testing', seconds: Math.round((i * TEST_EVERY_MS) / 1000) });
    const r = await api.testLine(id).catch(() => null);
    if (r?.status.registration === 'Registered' || r?.verified) return { phase: 'ok' };
    if (r?.status.registration === 'Rejected') return { phase: 'rejected' };
    if (r?.status.registration === 'unknown' && i >= 2) return { phase: 'unknown' };
    await new Promise((res) => setTimeout(res, TEST_EVERY_MS));
  }
  return { phase: 'waiting' };
};

const TestResult = ({ t, s }: { t: T; s: TestState }) => {
  if (s.phase === 'idle') return null;
  const kind = s.phase === 'ok' ? 'ok' : s.phase === 'rejected' ? 'error' : 'warn';
  return (
    <div className={`banner ${kind} small test-result`} role="status">
      {s.phase === 'testing' ? t('lines.test.running', { s: s.seconds ?? 0 }) : t(`lines.test.${s.phase}`)}
    </div>
  );
};

const blank = (p?: { id: string; host: string; port: number }): LineInput => ({ provider: p?.id ?? 'sip', label: '', host: p?.host ?? '', port: p?.port ?? 5060, username: '', authUsername: '', password: '' });
const lineName = (l: Line, t: T) => l.label || t(`lines.provider.${l.provider}`);

/** The SIP account fields (provider, user, password, server). Editing never shows the stored password. */
const LineFields = ({ t, view, x, set, editing }: { t: T; view: TelephonyView; x: LineInput; set: (p: Partial<LineInput>) => void; editing: boolean }) => {
  const [advanced, setAdvanced] = useState(Boolean(x.authUsername));
  const pick = (id: string) => {
    const p = view.providers.find((q) => q.id === id);
    set({ provider: id, host: p?.host ?? '', port: p?.port ?? 5060 });
  };
  return (
    <>
      {!editing && (
        <div className="chips">
          {view.providers.map((p) => (
            <button key={p.id} type="button" className={`chip ${x.provider === p.id ? 'active' : ''}`} onClick={() => pick(p.id)}>{t(`lines.provider.${p.id}`)}</button>
          ))}
        </div>
      )}
      <p className="hint">{t(`lines.provider.${x.provider}.hint`)}</p>
      <div className="form-grid">
        <Field label={t('lines.username')} hint={t('lines.username.hint')}><input value={x.username} autoComplete="off" onChange={(e) => set({ username: e.target.value })} /></Field>
        <Field label={t('lines.password')} {...(editing ? { hint: t('lines.password.keep') } : {})}>
          <input type="password" value={x.password} autoComplete="new-password" onChange={(e) => set({ password: e.target.value })} />
        </Field>
        <Field label={t('lines.host')}>
          <span className="row nowrap">
            <input value={x.host} placeholder="sip.example.com" onChange={(e) => set({ host: e.target.value })} />
            <input className="port" type="number" value={x.port} aria-label={t('lines.port')} onChange={(e) => set({ port: Number(e.target.value) || 5060 })} />
          </span>
        </Field>
        <Field label={t('lines.label')} hint={t('lines.label.hint')}><input value={x.label} onChange={(e) => set({ label: e.target.value })} /></Field>
      </div>
      {advanced ? (
        <div className="form-grid section-gap">
          <Field label={t('lines.authUsername')} hint={t('lines.authUsername.hint')}><input value={x.authUsername ?? ''} autoComplete="off" onChange={(e) => set({ authUsername: e.target.value })} /></Field>
        </div>
      ) : (
        <button type="button" className="link small" onClick={() => setAdvanced(true)}>{t('lines.advanced')}</button>
      )}
      <p className="hint">{t('lines.secretNote')}</p>
    </>
  );
};

/** A line user made only of digits is usually the number itself: offered as a suggestion, never assumed. */
const suggestE164 = (username: string) => (/^\d{8,15}$/u.test(username.trim()) ? `+${username.trim()}` : '');

type WizardResult = { lineId: string; numberId: string };

/**
 * Add a number in one flow: 1) the SIP account (new, or one already connected), 2) the number,
 * 3) which agent answers it and which agents call from it.
 */
const NumberWizard = ({ api, t, view, agents, onDone, onCancel }: { api: Api; t: T; view: TelephonyView; agents: AgentRow[]; onDone: (r: WizardResult, applied: boolean) => void; onCancel: () => void }) => {
  const panelLines = view.accounts;
  const [step, setStep] = useState(1);
  const [useLine, setUseLine] = useState<string>(panelLines.length > 0 ? '' : 'new');
  const [line, setLine] = useState<LineInput>(() => blank(view.providers[0]));
  const [createdLine, setCreatedLine] = useState<{ id: string; applied: boolean } | null>(null);
  const [test, setTest] = useState<TestState>({ phase: 'idle' });
  const [info, setInfo] = useState<string>();
  const [testedWith, setTestedWith] = useState('');
  const [num, setNum] = useState({ e164: '', label: '' });
  const [inbound, setInbound] = useState('');
  const [outbound, setOutbound] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const published = agents.filter((a) => a.publishedPackageId);

  const digitsOf = (x: string) => x.replace(/\D/gu, '');
  const listed = (x: string) => digitsOf(x) !== '' && panelLines.some((l) => l.numbers.some((n) => digitsOf(n.e164) === digitsOf(x)));
  const lineReady = useLine && useLine !== 'new' ? true : Boolean(line.host.trim() && line.username.trim() && line.password);
  const creds = JSON.stringify([line.host, line.port, line.username, line.authUsername, line.password]);
  // A new account must be connected and tested first; editing its fields afterwards asks for a new test.
  const tested = useLine !== 'new' || (createdLine !== null && testedWith === creds && ['ok', 'waiting', 'unknown'].includes(test.phase));

  const connectAndTest = async () => {
    // The same account already connected here: use it instead of a second registration.
    const same = panelLines.find((l) => l.host === line.host.trim().toLowerCase() && l.username === line.username.trim());
    if (same && !createdLine) {
      setUseLine(same.id);
      setError(undefined);
      setInfo(t('numbers.sameAccount', { name: lineName(same, t) }));
      await testExisting(same.id);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      let id = createdLine?.id;
      const input = { ...line, authUsername: line.authUsername?.trim() || null };
      if (!id) {
        const r = await api.addLine(input);
        setCreatedLine(r);
        id = r.id;
      } else {
        await api.updateLine(id, { label: input.label, host: input.host, port: input.port, username: input.username, authUsername: input.authUsername, password: input.password });
      }
      setTestedWith(creds);
      setTest(await runLineTest(api, id, setTest));
    } catch (e) {
      setTest({ phase: 'idle' });
      setError(lineError(t, e));
    } finally {
      setBusy(false);
    }
  };
  const testExisting = async (id: string) => {
    setBusy(true);
    setTest(await runLineTest(api, id, setTest));
    setBusy(false);
  };
  const next1 = () => {
    if (useLine === 'new' && !num.e164) setNum((n) => ({ ...n, e164: suggestE164(line.username) }));
    if (useLine && useLine !== 'new' && !num.e164) {
      const suggestion = suggestE164(panelLines.find((l) => l.id === useLine)?.username ?? '');
      if (!listed(suggestion)) setNum((n) => ({ ...n, e164: suggestion }));
    }
    setStep(2);
  };

  const finish = async () => {
    setBusy(true);
    setError(undefined);
    try {
      let lineId = useLine !== 'new' ? useLine : createdLine?.id;
      let applied = createdLine?.applied ?? true;
      if (!lineId) {
        const r = await api.addLine({ ...line, authUsername: line.authUsername?.trim() || null });
        setCreatedLine(r);
        lineId = r.id;
        applied = r.applied;
      }
      const { id: numberId } = await api.addNumber(lineId, num);
      if (inbound) await api.updateNumber(numberId, { inboundAgentId: inbound });
      for (const a of outbound) await api.setAgentNumber(a, numberId);
      onDone({ lineId, numberId }, applied);
    } catch (e) {
      setError(lineError(t, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card wizard-card">
      <div className="row-between">
        <h3>{t('numbers.add')}</h3>
        <span className="steps">
          {[1, 2, 3].map((s) => <span key={s} className={`step-dot ${s === step ? 'active' : s < step ? 'done' : ''}`}>{s}. {t(`numbers.step.${s}`)}</span>)}
        </span>
      </div>

      {step === 1 && (
        <>
          {panelLines.length > 0 && (
            <div className="choice-list">
              {panelLines.map((l) => (
                <label key={l.id} className={`choice ${useLine === l.id ? 'active' : ''}`}>
                  <input type="radio" checked={useLine === l.id} disabled={busy} onChange={() => { setUseLine(l.id); setTest({ phase: 'idle' }); }} />
                  <span><strong>{lineName(l, t)}</strong><span className="hint mono"> {l.username} @ {l.host}</span></span>
                  <span className={`pill ${lineState(l, t).kind}`}>{lineState(l, t).text}</span>
                </label>
              ))}
              <label className={`choice ${useLine === 'new' ? 'active' : ''}`}>
                <input type="radio" checked={useLine === 'new'} disabled={busy} onChange={() => { setUseLine('new'); setTest({ phase: 'idle' }); setInfo(undefined); }} />
                <span><strong>{t('numbers.newAccount')}</strong><span className="hint"> {t('numbers.newAccount.hint')}</span></span>
              </label>
            </div>
          )}
          {useLine === 'new' && <LineFields t={t} view={view} x={line} set={(p) => setLine((v) => ({ ...v, ...p }))} editing={false} />}
          {useLine && useLine !== 'new' && test.phase === 'idle' && (
            <button type="button" className="secondary" disabled={busy} onClick={() => void testExisting(useLine)}>{t('lines.test')}</button>
          )}
          {info && <div className="banner ok small">{info}</div>}
          <TestResult t={t} s={test} />
        </>
      )}

      {step === 2 && listed(num.e164) && <div className="banner warn small">{t('numbers.alreadyListed')}</div>}
      {step === 2 && (
        <div className="form-grid">
          <Field label={t('lines.number')} hint={t('numbers.e164.hint')}><input className="mono" value={num.e164} autoFocus placeholder="+90 850 000 00 00" onChange={(e) => setNum({ ...num, e164: e.target.value })} /></Field>
          <Field label={t('lines.numberLabel')}><input value={num.label} placeholder={t('lines.numberLabel.placeholder')} onChange={(e) => setNum({ ...num, label: e.target.value })} /></Field>
        </div>
      )}

      {step === 3 && (
        <div className="form-grid">
          <Field label={t('numbers.inbound')} hint={t('numbers.inbound.hint')}>
            <select value={inbound} onChange={(e) => setInbound(e.target.value)}>
              <option value="">{t('lines.inbound.none')}</option>
              {published.map((a) => <option key={a.id} value={a.id}>{agentTitle(a)}</option>)}
            </select>
          </Field>
          <div className="form-field">
            <span className="label">{t('numbers.outbound')}</span>
            <div className="check-list">
              {agents.length === 0 && <span className="hint">{t('numbers.noAgents')}</span>}
              {agents.map((a) => {
                const current = view.agentNumbers[a.id];
                const currentNumber = current ? view.accounts.flatMap((l) => l.numbers).find((n) => n.id === current)?.e164 : undefined;
                return (
                  <label key={a.id} className="check">
                    <input type="checkbox" checked={outbound.includes(a.id)} onChange={(e) => setOutbound((o) => (e.target.checked ? [...o, a.id] : o.filter((x) => x !== a.id)))} />
                    <span>{agentTitle(a)}{currentNumber ? <span className="hint"> · {t('numbers.nowUses', { number: currentNumber })}</span> : null}</span>
                  </label>
                );
              })}
            </div>
            <span className="hint">{t('numbers.outbound.hint')}</span>
          </div>
        </div>
      )}

      {error && <div className="banner error small">{error}</div>}
      <div className="row-between section-gap">
        <button type="button" className="ghost" onClick={step === 1 ? onCancel : () => setStep(step - 1)}>{step === 1 ? t('settings.discard') : t('numbers.back')}</button>
        {step === 1 && useLine === 'new' && !tested ? (
          <button type="button" className="primary" disabled={busy || !lineReady} onClick={() => void connectAndTest()}>{busy ? t('lines.test.busy') : test.phase === 'rejected' ? t('lines.test.again') : t('lines.connectTest')}</button>
        ) : step < 3 ? (
          <button type="button" className="primary" disabled={busy || (step === 1 ? !lineReady || !useLine : !num.e164.trim() || listed(num.e164))} onClick={() => (step === 1 ? next1() : setStep(3))}>{t('numbers.next')}</button>
        ) : (
          <button type="button" className="primary" disabled={busy} onClick={() => void finish()}>{busy ? t('settings.saving') : t('numbers.finish')}</button>
        )}
      </div>
    </div>
  );
};

/** Edit an existing SIP account. */
const LineEdit = ({ t, view, line, busy, onSave, onCancel }: { t: T; view: TelephonyView; line: Line; busy: boolean; onSave: (x: LineInput) => void; onCancel: () => void }) => {
  const [x, setX] = useState<LineInput>({ provider: line.provider, label: line.label, host: line.host, port: line.port, username: line.username, authUsername: line.authUsername ?? '', password: '' });
  return (
    <div className="card line-form">
      <h3>{t('lines.edit')} · {lineName(line, t)}</h3>
      <LineFields t={t} view={view} x={x} set={(p) => setX((v) => ({ ...v, ...p }))} editing />
      <div className="row-between section-gap">
        <span />
        <span className="row">
          <button type="button" className="ghost" onClick={onCancel}>{t('settings.discard')}</button>
          <button type="button" className="primary" disabled={busy || !x.host.trim() || !x.username.trim()} onClick={() => onSave(x)}>{busy ? t('settings.saving') : t('lines.save')}</button>
        </span>
      </div>
    </div>
  );
};

/**
 * ADR-030: the business's phone numbers. Each number sits on one of its SIP accounts; one agent answers
 * it, any number of agents call from it. A business can have several accounts and several numbers.
 */
export const TelephonyPage = ({ api, t }: { api: Api; t: T }) => {
  const [view, setView] = useState<TelephonyView>();
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [wizard, setWizard] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  // BRAIN/34 P3: the number whose opening hours are being edited.
  const [planFor, setPlanFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>();

  const load = useCallback(() => {
    api.telephony().then(setView).catch((e: unknown) => setMsg({ kind: 'error', text: errorText(t, e) }));
    api.agents().then((r) => setAgents(r.items)).catch(() => undefined);
  }, [api, t]);
  useEffect(load, [load]);

  const run = async (f: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setMsg(undefined);
    try {
      const r = await f();
      const applied = (r as { applied?: boolean } | undefined)?.applied;
      if (applied === false) setMsg({ kind: 'warn', text: t('lines.notApplied') });
      else if (ok) setMsg({ kind: 'ok', text: ok });
      load();
      return true;
    } catch (e) {
      setMsg({ kind: 'error', text: lineError(t, e) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const testCard = async (id: string) => {
    setBusy(true);
    setMsg({ kind: 'warn', text: t('lines.test.running', { s: 0 }) });
    const r = await runLineTest(api, id, (s) => setMsg({ kind: 'warn', text: t('lines.test.running', { s: s.seconds ?? 0 }) }));
    setMsg({ kind: r.phase === 'ok' ? 'ok' : r.phase === 'rejected' ? 'error' : 'warn', text: t(`lines.test.${r.phase}`) });
    setBusy(false);
    load();
  };
  const numbers = (view?.accounts ?? []).flatMap((l) => l.numbers.map((n) => ({ ...n, line: l })));
  const callersOf = (numberId: string) => agents.filter((a) => view?.agentNumbers[a.id] === numberId);
  const edited = view?.accounts.find((a) => a.id === editing);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1>{t('numbers.title')}</h1>
          <p className="muted">{t('numbers.subtitle')}</p>
        </div>
        {view && !wizard && !editing && <button type="button" className="primary" onClick={() => setWizard(true)}>{t('numbers.add')}</button>}
      </header>
      {msg && <div className={`banner ${msg.kind} small`}>{msg.text}{msg.kind === 'warn' && <button type="button" className="link" onClick={() => void run(() => api.applyLines(), t('lines.applied'))}>{t('lines.retry')}</button>}</div>}

      {view && wizard && (
        <NumberWizard
          api={api} t={t} view={view} agents={agents} onCancel={() => setWizard(false)}
          onDone={(_r, applied) => {
            setWizard(false);
            setMsg(applied ? { kind: 'ok', text: t('numbers.added') } : { kind: 'warn', text: t('lines.notApplied') });
            load();
          }}
        />
      )}
      {view && edited && (
        <LineEdit
          t={t} view={view} line={edited} busy={busy} onCancel={() => setEditing(null)}
          onSave={(x) => void run(() => api.updateLine(edited.id, { label: x.label, host: x.host, port: x.port, username: x.username, authUsername: x.authUsername?.trim() || null, ...(x.password ? { password: x.password } : {}) }), t('lines.saved')).then((ok) => ok && setEditing(null))}
        />
      )}

      {view && planFor && numbers.some((n) => n.id === planFor) && (
        <NumberPlanCard
          api={api} t={t} view={view} agents={agents} number={numbers.find((n) => n.id === planFor)!}
          onCancel={() => setPlanFor(null)}
          onSaved={() => {
            setPlanFor(null);
            setMsg({ kind: 'ok', text: t('lines.saved') });
            load();
          }}
        />
      )}
      {view && numbers.length === 0 && !wizard && <div className="empty"><p>{t('numbers.empty')}</p></div>}
      {view && numbers.length > 0 && (
        <table className="table numbers">
          <thead><tr><th>{t('lines.number')}</th><th>{t('numbers.account')}</th><th>{t('numbers.inbound')}</th><th>{t('numbers.plan')}</th><th>{t('numbers.outbound')}</th><th /></tr></thead>
          <tbody>
            {numbers.map((n) => {
              const st = lineState(n.line, t);
              const callers = callersOf(n.id);
              return (
                <tr key={n.id}>
                  <td><div className="mono">{n.e164}</div>{n.label && <div className="hint">{n.label}</div>}</td>
                  <td><div>{lineName(n.line, t)}</div><span className={`pill ${st.kind}`}>{st.text}</span></td>
                  <td>
                    <select value={n.inboundAgentId ?? ''} disabled={busy} onChange={(e) => void run(() => api.updateNumber(n.id, { inboundAgentId: e.target.value || null }), t('lines.saved'))}>
                      <option value="">{t('lines.inbound.none')}</option>
                      {agents.map((a) => <option key={a.id} value={a.id} disabled={!a.publishedPackageId}>{agentTitle(a)}{a.publishedPackageId ? '' : ` (${t('lines.notPublished')})`}</option>)}
                    </select>
                  </td>
                  <td><PlanCell t={t} n={n} agents={agents} onEdit={() => setPlanFor(n.id)} /></td>
                  <td>
                    <div className="agent-chips">
                      {callers.map((a) => (
                        <span key={a.id} className="agent-chip">
                          {a.name}
                          <button type="button" aria-label={t('settings.remove')} disabled={busy} onClick={() => void run(() => api.setAgentNumber(a.id, null), t('lines.saved'))}>×</button>
                        </span>
                      ))}
                      <select className="add-agent" value="" disabled={busy} aria-label={t('numbers.addCaller')} onChange={(e) => e.target.value && void run(() => api.setAgentNumber(e.target.value, n.id), t('lines.saved'))}>
                        <option value="">{t('numbers.addCaller')}</option>
                        {agents.filter((a) => view.agentNumbers[a.id] !== n.id).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                  </td>
                  <td><DangerButton t={t} label={t('settings.remove')} onConfirm={() => void run(() => api.removeNumber(n.id))} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {view && numbers.length > 0 && <p className="hint">{t('numbers.defaultNote')}</p>}
      {view && numbers.length > 0 && <ClosedDatesCard api={api} t={t} dates={view.closedDates ?? []} onSaved={load} />}

      {view && view.accounts.length > 0 && (
        <div className="section-gap">
          <h2 className="section-title">{t('numbers.accounts')}</h2>
          <p className="hint">{t('numbers.accounts.hint')}</p>
          {view.accounts.map((l) => {
            const st = lineState(l, t);
            return (
              <div key={l.id} className="card line compact-line">
                <div className="row-between">
                  <div>
                    <div className="row">
                      <strong>{lineName(l, t)}</strong>
                      {l.kind === 'system' && <span className="badge not_required">{t('lines.system')}</span>}
                      <span className="hint">{t('numbers.count', { n: l.numbers.length })}</span>
                    </div>
                    <div className="hint mono">{l.username} @ {l.host}{l.port !== 5060 ? `:${l.port}` : ''}</div>
                    {l.kind === 'panel' && !l.verified && <div className="hint">{t('lines.unverified')}</div>}
                  </div>
                  <span className="row">
                    <span className={`pill ${st.kind}`}>{st.text}</span>
                    <button type="button" className="ghost" disabled={busy} onClick={() => void testCard(l.id)}>{t('lines.test')}</button>
                    {l.kind === 'panel' && <button type="button" className="ghost" onClick={() => setEditing(l.id)}>{t('lines.edit')}</button>}
                    {l.kind === 'panel' && <DangerButton t={t} label={t('settings.remove')} onConfirm={() => void run(() => api.removeLine(l.id))} />}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {view && <WhatsAppAccountCard api={api} t={t} />}
      {view && <SmsAccountCard api={api} t={t} />}
    </section>
  );
};
